'use client';

import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import { useState, useEffect, useRef } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'motion/react';
import { mobileFloatingSignupLabel, signUpCtaLabel } from '@/lib/invite-only';
const NAV_LINKS = [{
  href: '#features',
  label: 'Features'
}, {
  href: '#how-it-works',
  label: 'How It Works'
}, {
  href: '#faq',
  label: 'FAQ'
}, {
  href: '/blog',
  label: 'Blog'
}] as const;
function scrollToSection(e: React.MouseEvent<HTMLAnchorElement>, href: string) {
  if (href.startsWith('#')) {
    e.preventDefault();
    const id = href.slice(1);
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth'
    });
  }
}
export function LandingNav(t0) {
  const $ = _c(36);
  const {
    onOpenDemo
  } = t0;
  const [scrollDir, setScrollDir] = useState("up");
  const [scrollY, setScrollY] = useState(0);
  const prevY = useRef(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const handleScroll = () => {
        const y = window.scrollY;
        setScrollDir(y > prevY.current ? "down" : "up");
        prevY.current = y;
        setScrollY(y);
      };
      prevY.current = window.scrollY;
      window.addEventListener("scroll", handleScroll, {
        passive: true
      });
      return () => window.removeEventListener("scroll", handleScroll);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const showMobileBar = scrollDir === "up" || scrollY < 80;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[100] focus:rounded focus:bg-[var(--landing-accent-red)] focus:px-3 focus:py-2 focus:text-white focus:outline-none focus:ring-2 focus:ring-white">Skip to content</a>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  let t5;
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Link href="/" className="flex items-center gap-2 rounded-xl px-4 py-2.5 font-heading text-sm font-bold text-white transition hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--landing-accent-blue)] focus-visible:ring-offset-2 focus-visible:ring-offset-black">On set screen</Link>;
    t5 = NAV_LINKS.map(_temp);
    t6 = <Link href="/login" className="rounded-xl px-4 py-2.5 text-sm text-white/85 transition hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--landing-accent-blue)] focus-visible:ring-offset-2 focus-visible:ring-offset-black">Log In</Link>;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
  } else {
    t4 = $[3];
    t5 = $[4];
    t6 = $[5];
  }
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="fixed bottom-0 left-0 right-0 z-50 pointer-events-none"><div className="pointer-events-auto hidden md:block"><div className="flex justify-center pb-6"><div className="flex items-center gap-1 rounded-2xl border border-[var(--landing-border)] bg-black/85 px-2 py-2 shadow-2xl backdrop-blur-xl">{t4}{t5}{t6}<Link href="/signup" className="rounded-xl bg-[var(--landing-accent-red)] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_0_20px_-4px_rgba(255,77,77,0.4)] transition hover:opacity-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--landing-accent-red)] focus-visible:ring-offset-2 focus-visible:ring-offset-black">{signUpCtaLabel()}</Link></div></div></div></div>;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const t8 = showMobileBar ? 0 : -100;
  const t9 = showMobileBar ? 1 : 0;
  let t10;
  if ($[7] !== t8 || $[8] !== t9) {
    t10 = {
      y: t8,
      opacity: t9
    };
    $[7] = t8;
    $[8] = t9;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      duration: 0.3,
      ease: "easeOut"
    };
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Link href="/" className="font-heading text-lg font-bold text-white focus:outline-none focus:ring-2 focus:ring-[var(--landing-accent-blue)] focus:ring-offset-2 focus:ring-offset-black">On set screen</Link>;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  let t13;
  if ($[12] !== onOpenDemo) {
    t13 = <Button type="button" variant="ghost" size="sm" onClick={onOpenDemo} className="text-white hover:bg-white/10 hover:text-white">Demo</Button>;
    $[12] = onOpenDemo;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Link href="/signup" className="rounded-lg bg-[var(--landing-accent-red)] px-4 py-2 text-sm font-semibold text-white">{signUpCtaLabel()}</Link>;
    $[14] = t14;
  } else {
    t14 = $[14];
  }
  let t15;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setMobileMenuOpen(_temp2);
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  const t16 = mobileMenuOpen ? "Close menu" : "Open menu";
  let t17;
  if ($[16] !== mobileMenuOpen) {
    t17 = mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />;
    $[16] = mobileMenuOpen;
    $[17] = t17;
  } else {
    t17 = $[17];
  }
  let t18;
  if ($[18] !== mobileMenuOpen || $[19] !== t16 || $[20] !== t17) {
    t18 = <Button type="button" variant="ghost" size="icon" className="text-white hover:bg-white/10 hover:text-white" onClick={t15} aria-expanded={mobileMenuOpen} aria-controls="mobile-menu" aria-label={t16}>{t17}</Button>;
    $[18] = mobileMenuOpen;
    $[19] = t16;
    $[20] = t17;
    $[21] = t18;
  } else {
    t18 = $[21];
  }
  let t19;
  if ($[22] !== t13 || $[23] !== t18) {
    t19 = <nav className="flex items-center justify-between px-4 py-3" aria-label="Main">{t12}<div className="flex items-center gap-2">{t13}{t14}{t18}</div></nav>;
    $[22] = t13;
    $[23] = t18;
    $[24] = t19;
  } else {
    t19 = $[24];
  }
  const t20 = `border-t border-[var(--landing-border)] bg-black/95 ${mobileMenuOpen ? "block" : "hidden"}`;
  const t21 = !mobileMenuOpen;
  let t22;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="flex flex-col gap-1 px-4 py-4">{NAV_LINKS.map(t23 => {
        const {
          href: href_0,
          label: label_0
        } = t23;
        return <Link key={href_0} href={href_0} onClick={e_0 => {
          scrollToSection(e_0, href_0);
          setMobileMenuOpen(false);
        }} className="rounded-lg px-3 py-2.5 text-white hover:bg-white/10">{label_0}</Link>;
      })}<Link href="/login" className="rounded-lg px-3 py-2.5 text-white hover:bg-white/10">Log In</Link></div>;
    $[25] = t22;
  } else {
    t22 = $[25];
  }
  let t23;
  if ($[26] !== t20 || $[27] !== t21) {
    t23 = <div id="mobile-menu" className={t20} aria-hidden={t21}>{t22}</div>;
    $[26] = t20;
    $[27] = t21;
    $[28] = t23;
  } else {
    t23 = $[28];
  }
  let t24;
  if ($[29] !== t10 || $[30] !== t19 || $[31] !== t23) {
    t24 = <AnimatePresence><motion.header initial={false} animate={t10} transition={t11} className="fixed left-0 right-0 top-0 z-50 border-b border-[var(--landing-border)] bg-black/90 backdrop-blur-md md:hidden">{t19}{t23}</motion.header></AnimatePresence>;
    $[29] = t10;
    $[30] = t19;
    $[31] = t23;
    $[32] = t24;
  } else {
    t24 = $[32];
  }
  let t25;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <div className="fixed bottom-6 left-4 right-4 z-40 md:hidden"><Link href="/signup" className="block rounded-full bg-[var(--landing-accent-red)] py-3.5 text-center font-semibold text-white shadow-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black">{mobileFloatingSignupLabel()}</Link></div>;
    $[33] = t25;
  } else {
    t25 = $[33];
  }
  let t26;
  if ($[34] !== t24) {
    t26 = <>{t3}{t7}{t24}{t25}</>;
    $[34] = t24;
    $[35] = t26;
  } else {
    t26 = $[35];
  }
  return t26;
}
function _temp2(o) {
  return !o;
}
function _temp(t0) {
  const {
    href,
    label
  } = t0;
  return <Link key={href} href={href} onClick={e => scrollToSection(e, href)} className="rounded-xl px-4 py-2.5 text-sm text-white/85 transition hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--landing-accent-blue)] focus-visible:ring-offset-2 focus-visible:ring-offset-black">{label}</Link>;
}